import { Component, effect, EventEmitter, Input, input, OnDestroy, Output } from '@angular/core';
import {
    AbstractControl,
    ControlValueAccessor,
    FormControl,
    FormGroup,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR,
    ReactiveFormsModule,
    ValidationErrors,
    Validator,
} from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ListingsService } from 'listings/services/listings.service';
import remove from 'lodash-es/remove';
import { Subscription } from 'rxjs';
import { FormErrorComponent } from 'shared/components/form-error/form-error.component';
import { PERMISSIONS_HIERARCHY } from 'shared/constants/general-constants';
import { FeatureFlagEnum } from 'shared/interfaces/environment.interface';
import { FeatureGroup } from 'shared/interfaces/feature-group.interface';
import { isCurrentSupplierUser } from 'shared/interfaces/current-supplier-user.interface';
import { ApplicationPermissionArray } from 'shared/interfaces/permissions.interface';
import { FeatureFlagService } from 'shared/services/feature-flag.service';
import { UserApiService } from 'shared/services/userApi.service';
import { unsubscribeFromAll } from 'shared/util/unsubscribeFromAll';
import { SupplierUserEditVisualsPermissionsComponent } from 'users/components/supplier-users/supplier-user-edit-visuals-permissions/supplier-user-edit-visuals-permissions.component';
import { VisualPermissionResponse } from 'users/interfaces/visual-permissions.interface';
import { VisualsPermissionsService } from 'visuals/services/visuals-permissions.service';

const VISUAL_FEATURE_GROUP_NAME = 'Visuals';

@Component({
    selector: 'app-permissions-control',
    templateUrl: './permissions-control.component.html',
    styleUrls: ['./permissions-control.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: PermissionsControlComponent,
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: PermissionsControlComponent,
            multi: true,
        },
    ],
    imports: [ReactiveFormsModule, FormErrorComponent, TranslateModule, SupplierUserEditVisualsPermissionsComponent],
})
export class PermissionsControlComponent implements OnDestroy, ControlValueAccessor, Validator {
    @Output() featureGroupChange: EventEmitter<FeatureGroup[]> = new EventEmitter<FeatureGroup[]>();

    @Input() userId?: string;
    @Input() userGroupId?: string;
    @Input() set supplierId(val: string) {
        if (this._supplierId !== val) {
            this._supplierId = val;
            this.supplierIdChanged();
        }
    }
    get supplierId() {
        return this._supplierId;
    }
    visualPermissions = input<VisualPermissionResponse[]>([]);
    supplierUserOrUserGroupHasAccessToVisualsArea = input<boolean>(false);

    private _supplierId!: string;
    private onChange!: Function;
    private onTouched!: Function;
    private onValidatorChange!: Function;
    private subscriptions: { [key: string]: Subscription | undefined } = {};
    private permissionZoneToIdMappings: { [key: string]: string } = {};
    private permissionResourceIdToIdMappings: { [key: string]: string } = {};
    private mainListingResource!: FeatureGroup;
    private listingCustomFieldsResource!: FeatureGroup;
    private permissionZoneToInputMappings: { [key: string]: string } = {
        ACCESS_TO_USERS: 'users',
        ACCESS_TO_USER_GROUPS: 'userGroups',
        ACCESS_TO_LISTINGS_ACCESS_GROUPS: 'accessGroups',
        ACCESS_TO_BULK_OPERATIONS: 'accessToBulkOperations',
        ACCESS_TO_CHANNEL_SNAPSHOT_REGENERATION: 'accessToChannelSnapshotRegeneration',
    };
    private inputToRowTranslationKeyMappings: { [key: string]: string } = {
        listings: 'listings',
        customFields: 'customFields',
        users: 'users',
        userGroups: 'userGroups',
        accessGroups: 'accessGroups',
        accessToBulkOperations: 'bulkOperation',
        accessToChannelSnapshotRegeneration: 'regenerateSnapshots',
    };
    private storedPermissions: ApplicationPermissionArray | null = null;
    // Every feature group that has a form control, including the ones the template does not render
    private allFeatureGroups: FeatureGroup[] = [];

    protected readonly featureFlagEnum = FeatureFlagEnum;

    public featureGroups!: FeatureGroup[];
    public formGroup = new FormGroup({
        permissions: new FormGroup(
            {
                listings: new FormGroup(
                    {
                        listings: new FormGroup({
                            read: new FormControl(false),
                            edit: new FormControl(false),
                            create: new FormControl(false),
                            delete: new FormControl(false),
                            publish: new FormControl(false),
                        }),
                        customFields: new FormGroup({
                            create: new FormControl(false),
                        }),
                    },
                    this.validateListingsPermissions.bind(this),
                ),
                users: new FormGroup({
                    users: new FormGroup({
                        read: new FormControl(false),
                        edit: new FormControl(false),
                        invite: new FormControl(false),
                    }),
                    userGroups: new FormGroup({
                        read: new FormControl(false),
                        edit: new FormControl(false),
                    }),
                    accessGroups: new FormGroup({
                        read: new FormControl(false),
                        edit: new FormControl(false),
                    }),
                    accessToBulkOperations: new FormGroup({
                        allow: new FormControl(false),
                    }),
                    accessToChannelSnapshotRegeneration: new FormGroup({
                        allow: new FormControl(false),
                    }),
                }),
            },
            this.validateListingsPermissions.bind(this),
        ),
    });

    constructor(
        private listingsService: ListingsService,
        public featureFlagService: FeatureFlagService,
        private userApiService: UserApiService,
        private translateService: TranslateService,
        private visualsPermissionsService: VisualsPermissionsService,
    ) {
        effect(() => {
            this.visualsPermissionsService.notAllowedVisualPermissionChanges();
            this.onValidatorChange?.();
        });
    }

    public supplierIdChanged() {
        unsubscribeFromAll(this.subscriptions);
        this.formGroup.reset();

        if (!this.supplierId) {
            return;
        }

        this.subscriptions.getFeatureGroups = this.listingsService.getFeatureGroupsResources(this.supplierId).subscribe((featureGroups) => {
            const listingsGroup = this.formGroup.get('permissions.listings') as FormGroup;
            const onInitialLoad = true;
            this.featureGroupChange.emit(featureGroups);
            this.mainListingResource = remove(featureGroups, (x) => x.isListing)[0];
            this.listingCustomFieldsResource = remove(featureGroups, (x) => x.isCustomFields)[0];

            for (let i = 0; i < featureGroups.length; i++) {
                const featureGroup = featureGroups[i];

                listingsGroup.addControl(
                    featureGroup.id as string,
                    new FormGroup({
                        read: new FormControl({ value: false, disabled: true }),
                        edit: new FormControl({ value: false, disabled: true }),
                    }),
                    { emitEvent: false },
                );
                this.subscriptions.listingsGroupBind = listingsGroup
                    .get(featureGroup.id as string)
                    ?.valueChanges.subscribe(this.selectIncludedCheckboxes.bind(this, listingsGroup, featureGroup.id ?? ''));
            }

            for (let i = 0; i < featureGroups.length; i++) {
                const featureGroup = featureGroups[i];
                const formGroup = listingsGroup.get(featureGroup.id as string) as FormGroup | null;
                if (formGroup) {
                    this.selectIncludedCheckboxes(
                        listingsGroup,
                        featureGroup.id as string,
                        formGroup.value as { [key: string]: boolean },
                        onInitialLoad,
                    );
                }
            }

            const mainListings = listingsGroup.get('listings') as FormGroup | null;
            if (mainListings) {
                this.selectIncludedCheckboxes(listingsGroup, 'listings', mainListings.value as { [key: string]: boolean }, onInitialLoad);

                this.subscriptions.mainListings = mainListings?.valueChanges.subscribe((values) => {
                    const accessTypeIndex = PERMISSIONS_HIERARCHY.findIndex(
                        (item) => item === PermissionsControlComponent.getHighestRankingPermissionFromValue(values).toLowerCase(),
                    );
                    const createIndex = PERMISSIONS_HIERARCHY.findIndex((item) => item === 'create');
                    if (accessTypeIndex !== -1 && createIndex !== -1 && accessTypeIndex >= createIndex) {
                        const listingProfileKey = this.featureGroups?.find(
                            (featureGroup) => featureGroup.name?.toLowerCase().replaceAll(' ', '') === 'listingprofile',
                        )?.id;
                        const listingProfileReadControl = this.formGroup?.get(
                            `permissions.listings.${listingProfileKey}.read`,
                        ) as AbstractControl<boolean>;

                        if (listingProfileReadControl) {
                            if (!listingProfileReadControl.value) {
                                listingProfileReadControl.setValue(true);
                            }
                        }
                    }
                });
            }
            const customFields = listingsGroup.get('customFields') as FormGroup | null;
            if (customFields) {
                this.selectIncludedCheckboxes(listingsGroup, 'customFields', customFields.value as { [k: string]: boolean }, onInitialLoad);
            }

            this.allFeatureGroups = featureGroups;

            if (
                this.featureFlagService.featureFlags()[FeatureFlagEnum.CONTENT_VISUALS_SPLIT] &&
                this.supplierUserOrUserGroupHasAccessToVisualsArea()
            ) {
                this.featureGroups = featureGroups.filter((featureGroup) => featureGroup.name !== VISUAL_FEATURE_GROUP_NAME);
            } else {
                this.featureGroups = featureGroups;
            }

            // This will automatically re-tick the listingProfile->READ if listings access >= CREATE without disabling the checkbox
            // If the control would instead be disabled, its value won't be submitted when saving the form.
            this.subscriptions.listingsGroup = listingsGroup.valueChanges.subscribe((values) => {
                const listingProfileKey = this.featureGroups?.find(
                    (featureGroup) => featureGroup.name?.toLowerCase().replaceAll(' ', '') === 'listingprofile',
                )?.id;
                const accessTypeIndex = PERMISSIONS_HIERARCHY.findIndex(
                    (item) => item === PermissionsControlComponent.getHighestRankingPermissionFromValue(values.listings).toLowerCase(),
                );
                const createIndex = PERMISSIONS_HIERARCHY.findIndex((item) => item === 'create');

                if (listingProfileKey && accessTypeIndex !== -1 && createIndex !== -1 && accessTypeIndex >= createIndex) {
                    const listingProfileReadControl = this.formGroup?.get(
                        `permissions.listings.${listingProfileKey}.read`,
                    ) as AbstractControl<boolean>;
                    if (listingProfileReadControl) {
                        if (!listingProfileReadControl.value) {
                            listingProfileReadControl.setValue(true);
                        }
                    }
                }
            });
        });

        const listingsControls = this.formGroup.get('permissions.listings') as FormGroup;
        const usersCheckboxControls = this.formGroup.get('permissions.users') as FormGroup;

        this.subscriptions.listingsControl = listingsControls
            .get('listings')
            ?.valueChanges.subscribe(this.selectIncludedCheckboxes.bind(this, listingsControls, 'listings'));

        ['users', 'userGroups', 'accessGroups', 'accessToBulkOperations', 'accessToChannelSnapshotRegeneration'].forEach((controlName) => {
            this.subscriptions.usersCheckboxControls = usersCheckboxControls
                .get(controlName)
                ?.valueChanges.subscribe(this.selectIncludedCheckboxes.bind(this, usersCheckboxControls, controlName));
        });

        this.subscriptions.permissions = this.formGroup.get('permissions')?.valueChanges.subscribe((value) => {
            this.onChange(this.prepareValueForSaving(value));
            this.onTouched();
        });
    }

    public ngOnDestroy() {
        unsubscribeFromAll(this.subscriptions);
    }

    public registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    public registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    public registerOnValidatorChange?(fn: () => void): void {
        this.onValidatorChange = fn;
    }

    public validate(): ValidationErrors | null {
        const ctr = this.formGroup.get('permissions.listings');
        if (ctr) {
            Object.keys(ctr.value).forEach((k) => {
                const childCtr = this.formGroup.get('permissions.listings.' + k);
                childCtr?.markAllAsTouched();
                childCtr?.markAsDirty();
            });
        }
        const errors: ValidationErrors = {};
        const notAllowedPermissions = this.getNotAllowedPermissionChanges();

        if (notAllowedPermissions.length) {
            errors['notAllowedPermissions'] = { permissions: notAllowedPermissions.join(', ') };
        }

        if (this.formGroup?.invalid) {
            errors['invalid'] = true;
        }

        return Object.keys(errors).length ? errors : null;
    }

    public writeValue(value: ApplicationPermissionArray): void {
        this.storedPermissions = value;

        if (value === null) {
            this.subscriptions.getFeatureGroups?.add(() => this.selectAllCheckboxes());
        }

        // We only add the values once the feature groups have loaded
        // because that triggers adding form inputs to the form group
        if (value !== null) {
            this.subscriptions.getFeatureGroups?.add(() => {
                this.writeToCheckboxes(value);
            });
        }
    }

    public setDisabledState(isDisabled: boolean) {}

    private writeToCheckboxes(value: ApplicationPermissionArray) {
        const listingsControls = this.formGroup.get('permissions.listings')!;
        const usersCheckboxControls = this.formGroup.get('permissions.users') as FormGroup;

        // Select all inferior checkboxes of the corresponding access checkbox
        for (let i = 0; i < value[0].length; i++) {
            const resourcePermission = value[0][i];

            if (!resourcePermission.accessType) {
                continue;
            }

            if (
                this.mainListingResource?.id === resourcePermission.resourceId ||
                //fallback compatibility?
                !resourcePermission.resourceId
            ) {
                const valueObject: { [key: string]: boolean } = {};

                valueObject[resourcePermission.accessType.toLowerCase()] = true;

                this.selectIncludedCheckboxes(listingsControls as FormGroup, 'listings', valueObject);

                if (resourcePermission.id) {
                    this.permissionResourceIdToIdMappings['null'] = resourcePermission.id;
                }
            } else if (
                this.listingCustomFieldsResource?.id === resourcePermission.resourceId ||
                //fallback compatibility?
                !resourcePermission.resourceId
            ) {
                const valueObject: { [key: string]: boolean } = {};

                valueObject[resourcePermission.accessType.toLowerCase()] = true;

                this.selectIncludedCheckboxes(listingsControls as FormGroup, 'customFields', valueObject);

                if (resourcePermission.id) {
                    this.permissionResourceIdToIdMappings['undefined'] = resourcePermission.id;
                }
            } else if (resourcePermission.resourceId) {
                const valueObject: { [key: string]: boolean } = {};

                valueObject[resourcePermission.accessType.toLowerCase()] = true;

                this.selectIncludedCheckboxes(listingsControls as FormGroup, resourcePermission.resourceId, valueObject);

                if (resourcePermission.id) {
                    this.permissionResourceIdToIdMappings[resourcePermission.resourceId] = resourcePermission.id;
                }
            }
        }

        const controlParents = [usersCheckboxControls];

        for (let i = 0; i < value[1].length; i++) {
            const userGroupPermission = value[1][i];

            if (!userGroupPermission.accessType || !userGroupPermission.permissionAccessZone) {
                continue;
            }

            const userControl = usersCheckboxControls.get(this.permissionZoneToInputMappings[userGroupPermission.permissionAccessZone]);
            const formIndex: number | undefined = userControl ? 0 : undefined;
            const checkboxGroup = userControl;
            const valueObject: { [key: string]: boolean } = {};

            valueObject[userGroupPermission.accessType.toLowerCase()] = true;

            if (userGroupPermission.id) {
                this.permissionZoneToIdMappings[userGroupPermission.permissionAccessZone] = userGroupPermission.id;
            }

            if (checkboxGroup && formIndex !== undefined) {
                this.selectIncludedCheckboxes(
                    controlParents[formIndex],
                    this.permissionZoneToInputMappings[userGroupPermission.permissionAccessZone],
                    valueObject,
                );
            }
        }

        listingsControls.markAsUntouched();
        usersCheckboxControls.markAsUntouched();
    }

    /**
     * Checks and enables/disables checkboxes that are hierarchically beneath the given value object.
     * @param controlParent
     * @param controlName
     * @param value
     */
    public selectIncludedCheckboxes(
        controlParent: FormGroup,
        controlName: string,
        value: {
            [key: string]: boolean;
        },
        onInitialLoad: boolean = false,
    ) {
        let firstCheckedIndex = -1;

        if (!PERMISSIONS_HIERARCHY) {
            throw new ReferenceError('There is no defined user hierarchy');
        }

        /*
        When loading changes from the outside: value = {[permissionKey]: true}
        When clicking on a row without permissions: value = {[everyAvailablePermissionKey]: boolean}
        When unchecking the leading checkbox: value = {[everyNonDisabledKey]: boolean}
         */
        if (value) {
            //<editor-fold desc="Find the checkbox that was changed">
            const permissionKeys = Object.keys(value);
            let permissionKeyIndex: number = -1;

            // When not loading outside info
            if (permissionKeys.length > 1) {
                permissionKeyIndex = permissionKeys.findIndexFromEnd((item) => value[item]);

                if (permissionKeyIndex === -1) {
                    permissionKeyIndex = 0;
                }
            } else {
                permissionKeyIndex = 0;
            }
            //</editor-fold>

            //<editor-fold desc="Find the hierarchical index of the affected checkbox">
            const tempIndex = PERMISSIONS_HIERARCHY.findIndex((item) => item === permissionKeys[permissionKeyIndex]);

            for (let i = tempIndex; i >= 0; i--) {
                // If the control exists
                if (controlParent.get(controlName)?.get(PERMISSIONS_HIERARCHY[i])) {
                    // And the value is true
                    if (value[permissionKeys[permissionKeyIndex]]) {
                        firstCheckedIndex = i;
                    } else {
                        // If the value is not true, search for the next existing control in the hierarchy
                        for (let j = i - 1; j >= 0; j--) {
                            if (controlParent.get(controlName)?.get(PERMISSIONS_HIERARCHY[j])) {
                                firstCheckedIndex = j;
                                break;
                            }
                        }
                    }
                    break;
                }
            }
            //</editor-fold>
        }

        //<editor-fold desc="Enable and check all the checkboxes below, enable the last checkbox and disable the rest">
        if (firstCheckedIndex !== -1) {
            const control = controlParent.get(controlName);

            control?.get(PERMISSIONS_HIERARCHY[firstCheckedIndex])?.setValue(true, { emitEvent: false });
            control?.get(PERMISSIONS_HIERARCHY[firstCheckedIndex])?.enable({ emitEvent: false });

            if (control) {
                for (let i = firstCheckedIndex - 1; i >= 0; i--) {
                    const permissionType = PERMISSIONS_HIERARCHY[i];

                    control.get(permissionType)?.setValue(true, { emitEvent: false });
                    control.get(permissionType)?.disable({ emitEvent: false });
                }

                for (let i = firstCheckedIndex + 1; i < PERMISSIONS_HIERARCHY.length; i++) {
                    const permissionType = PERMISSIONS_HIERARCHY[i];
                    const controlByPermissionType = control.get(permissionType);

                    const globalGroup = this.formGroup.get('permissions.listings.listings') as FormGroup | null;
                    const globalRead = !!globalGroup?.get('read')?.value;
                    const globalEdit = !!globalGroup?.get('edit')?.value;

                    const shouldEnableRead = (permissionType === 'read' && globalRead) || onInitialLoad;
                    const shouldEnableEdit = (permissionType === 'edit' && globalEdit) || onInitialLoad;

                    if (controlByPermissionType && (shouldEnableRead || shouldEnableEdit)) {
                        controlByPermissionType.enable({ emitEvent: false });
                    }
                }
            }
        } else {
            // Nothing selected, so we leave the row fully interactable according to the listings' checked value, so the user can pick higher levels directly.
            const group = controlParent.get(controlName) as FormGroup | null;
            if (group) {
                const existingKeys = PERMISSIONS_HIERARCHY.filter((k) => group.get(k));
                const listingProfileId = this.featureGroups?.find(
                    (featureGroup) => featureGroup.name?.toLowerCase().replaceAll(' ', '') === 'listingprofile',
                )?.id;
                const listingsPermission = PermissionsControlComponent.getHighestRankingPermissionFromValue(
                    this.formGroup?.get('permissions.listings.listings')?.value as { [key: string]: boolean },
                );
                const listingsPermissionIndex = PERMISSIONS_HIERARCHY.findIndex((item) => item === listingsPermission.toLowerCase());
                const createIndex = PERMISSIONS_HIERARCHY.findIndex((item) => item === 'create');
                const canEnableBase = controlName !== listingProfileId || listingsPermissionIndex < createIndex;

                const globalGroup = this.formGroup.get('permissions.listings.listings') as FormGroup | null;
                const globalRead = !!globalGroup?.get('read')?.value;
                const globalEdit = !!globalGroup?.get('edit')?.value;

                for (let i = 0; i < existingKeys.length; i++) {
                    const key = existingKeys[i];
                    const control = group.get(key);

                    const shouldEnableRead = (key === 'read' && globalRead) || onInitialLoad;
                    const shouldEnableEdit = (key === 'edit' && globalEdit) || onInitialLoad;

                    if (!control) continue;

                    if (i === 0) {
                        if (canEnableBase && (shouldEnableRead || shouldEnableEdit)) {
                            control.enable({ emitEvent: false });
                        }
                        control.setValue(false, { emitEvent: false });
                    } else {
                        if (shouldEnableRead || shouldEnableEdit) {
                            control.enable({ emitEvent: false });
                        }
                        control.setValue(false, { emitEvent: false });
                    }
                }
            }
        }
        //</editor-fold>
    }

    private selectAllCheckboxes() {
        const listingsControls = this.formGroup.get('permissions.listings') as FormGroup;
        const usersCheckboxControls = this.formGroup.get('permissions.users') as FormGroup;

        const actingUserAccessTypes = this.getActingUserAccessTypes();

        [listingsControls, usersCheckboxControls].forEach((controls) => {
            const controlKeys = Object.keys(controls.controls);

            for (let i = 0; i < controlKeys.length; i++) {
                const controlName = controlKeys[i];
                const control = controls.get(controlName) as FormGroup;
                const userGroupCustomFieldsScenario = controlName === 'customFields' && !this.userId;

                if (actingUserAccessTypes) {
                    const actingUserAccessType = actingUserAccessTypes[controlName];

                    if (actingUserAccessType && control.get(actingUserAccessType) && !userGroupCustomFieldsScenario) {
                        this.selectIncludedCheckboxes(controls, controlName, { [actingUserAccessType]: true });
                    }
                    continue;
                }

                for (let j = PERMISSIONS_HIERARCHY.length; j >= 0; j--) {
                    const permission = PERMISSIONS_HIERARCHY[j];

                    if (control.get(permission) && !userGroupCustomFieldsScenario) {
                        const valueObject: { [key: string]: true } = {};
                        valueObject[permission] = true;
                        this.selectIncludedCheckboxes(controls, controlName, valueObject);
                        break;
                    }
                }
            }
        });

        // Default Bulk access to false for user groups since it's an important permission
        usersCheckboxControls.get('accessToBulkOperations')?.get('allow')?.setValue(false, { emitEvent: false });

        this.onChange(this.prepareValueForSaving(this.formGroup.get('permissions')?.value));
    }

    /**
     * The acting user's own access type per checkbox group, keyed the same way the form groups are.
     * Returns null for Shiji and system users, who are not capped by their own permissions.
     */
    private getActingUserAccessTypes(): { [controlName: string]: string } | null {
        const currentUser = this.userApiService.currentUser;

        if (!currentUser || !isCurrentSupplierUser(currentUser)) {
            return null;
        }

        const accessTypes: { [controlName: string]: string } = {};

        (currentUser.resourcePermissions ?? []).forEach((resourcePermission) => {
            if (!resourcePermission.accessType) {
                return;
            }

            accessTypes[this.getResourceControlName(resourcePermission.resourceId)] = resourcePermission.accessType.toLowerCase();
        });

        (currentUser.internalPermissions ?? []).forEach((internalPermission) => {
            const controlName = this.permissionZoneToInputMappings[internalPermission.permissionAccessZone];

            if (controlName && internalPermission.accessType) {
                accessTypes[controlName] = internalPermission.accessType.toLowerCase();
            }
        });

        return accessTypes;
    }

    /**
     * The stored access type per checkbox group, taken from the value the control was written with.
     * Empty in the create flow, where nothing is stored yet.
     */
    private getStoredAccessTypes(): { [controlName: string]: string } {
        const accessTypes: { [controlName: string]: string } = {};

        if (!this.storedPermissions) {
            return accessTypes;
        }

        (this.storedPermissions[0] ?? []).forEach((resourcePermission) => {
            if (!resourcePermission.accessType) {
                return;
            }

            accessTypes[this.getResourceControlName(resourcePermission.resourceId)] = resourcePermission.accessType.toLowerCase();
        });

        (this.storedPermissions[1] ?? []).forEach((internalPermission) => {
            const controlName = this.permissionZoneToInputMappings[internalPermission.permissionAccessZone as string];

            if (controlName && internalPermission.accessType) {
                accessTypes[controlName] = internalPermission.accessType.toLowerCase();
            }
        });

        return accessTypes;
    }

    /**
     * The checkbox groups the acting user changed but is not allowed to set, named for display.
     * The server compares the highest of the stored and the new access type against the acting user's own,
     * so an untouched row is never a violation even when it sits above their level.
     */
    private getNotAllowedPermissionChanges(): string[] {
        const actingUserAccessTypes = this.getActingUserAccessTypes();

        if (!actingUserAccessTypes || !this.featureGroups) {
            return [];
        }

        const storedAccessTypes = this.getStoredAccessTypes();
        const notAllowedRows: string[] = [];

        [this.formGroup.get('permissions.listings') as FormGroup, this.formGroup.get('permissions.users') as FormGroup].forEach(
            (controls) => {
                if (!controls) {
                    return;
                }

                Object.keys(controls.controls).forEach((controlName) => {
                    if (controlName === 'customFields' && !this.userId) {
                        return;
                    }

                    const currentAccessType = PermissionsControlComponent.getHighestRankingPermissionFromValue(
                        (controls.get(controlName) as FormGroup).value as { [key: string]: boolean },
                    ).toLowerCase();
                    const storedAccessType = storedAccessTypes[controlName] ?? '';

                    if (currentAccessType === storedAccessType) {
                        return;
                    }

                    const requiredRank = Math.max(this.getAccessTypeRank(storedAccessType), this.getAccessTypeRank(currentAccessType));

                    if (requiredRank > this.getAccessTypeRank(actingUserAccessTypes[controlName])) {
                        notAllowedRows.push(this.getRowLabel(controlName));
                    }
                });
            },
        );

        return notAllowedRows.concat(this.getNotAllowedVisualPermissionChanges());
    }

    private getNotAllowedVisualPermissionChanges(): string[] {
        return this.visualsPermissionsService
            .notAllowedVisualPermissionChanges()
            .map((type) => this.translateService.instant(`users.supplierUsers.edit.visualPermissions.names.${type.toLowerCase()}`));
    }

    private getResourceControlName(resourceId?: string): string {
        if (resourceId === this.mainListingResource?.id || !resourceId) {
            return 'listings';
        }

        if (resourceId === this.listingCustomFieldsResource?.id) {
            return 'customFields';
        }

        return resourceId;
    }

    private getAccessTypeRank(accessType?: string): number {
        return accessType ? PERMISSIONS_HIERARCHY.indexOf(accessType.toLowerCase()) : -1;
    }

    private getRowLabel(controlName: string): string {
        const featureGroupName = this.allFeatureGroups.find((featureGroup) => featureGroup.id === controlName)?.name;

        if (featureGroupName) {
            return featureGroupName;
        }

        const translationKey = this.inputToRowTranslationKeyMappings[controlName];

        return translationKey
            ? this.translateService.instant(`users.userGroups.createEdit.accessSettings.headings.rows.${translationKey}`)
            : controlName;
    }

    private prepareValueForSaving(value: any): [[], []] {
        if (!PERMISSIONS_HIERARCHY) {
            throw new ReferenceError('There is no defined user hierarchy');
        }

        const listingKeys = Object.keys(value.listings);
        const finalArray: any = [[], []];
        const hasResourcePermissionIds = Boolean(Object.keys(this.permissionResourceIdToIdMappings).length);

        // Go over each listing / resourcePermission
        for (let i = 0; i < listingKeys.length; i++) {
            const key = listingKeys[i];
            const listing = value.listings[key];
            // Take only the highest ranking permission into account
            const accessType = PermissionsControlComponent.getHighestRankingPermissionFromValue(listing);

            if (!accessType) {
                continue;
            }

            const accessObject: any = {
                resourceId:
                    key === 'listings' ? this.mainListingResource?.id : key === 'customFields' ? this.listingCustomFieldsResource?.id : key,
                accessType: accessType,
            };

            if (hasResourcePermissionIds) {
                accessObject.id =
                    this.permissionResourceIdToIdMappings[key === 'listings' ? 'null' : key === 'customFields' ? 'undefined' : key];
            }

            if (this.userGroupId) {
                accessObject.userGroupId = this.userGroupId;
            }

            if (this.userId) {
                accessObject.userId = this.userId;
            }

            finalArray[0].push(accessObject);
        }

        // TODO: Think of a way to make this less hard-coded in case stuff changes
        const userGroupPermissionsValue: { [key: string]: { [key: string]: boolean } } = {
            users: value.users.users,
            userGroups: value.users.userGroups,
            accessGroups: value.users.accessGroups,
            accessToBulkOperations: value.users.accessToBulkOperations,
            accessToChannelSnapshotRegeneration: value.users.accessToChannelSnapshotRegeneration,
        };

        const inputMappingKeys = Object.keys(this.permissionZoneToInputMappings);
        const hasGroupPermissionIds = Boolean(inputMappingKeys.length);

        for (let i = 0; i < inputMappingKeys.length; i++) {
            const key = inputMappingKeys[i];
            const accessType = PermissionsControlComponent.getHighestRankingPermissionFromValue(
                userGroupPermissionsValue[this.permissionZoneToInputMappings[key]],
            );

            if (!accessType) {
                continue;
            }

            const accessObject: any = {
                permissionAccessZone: key,
                accessType: accessType,
            };

            if (this.userGroupId) {
                accessObject.userGroupId = this.userGroupId;
            }

            if (this.userId) {
                accessObject.userId = this.userId;
            }

            if (hasGroupPermissionIds) {
                accessObject.id = this.permissionZoneToIdMappings[key];
            }

            finalArray[1].push(accessObject);
        }

        return finalArray;
    }

    private validateListingsPermissions(control: AbstractControl) {
        //control == 'permissions.listings';
        const allKeys = Object.keys(control.value);
        if (!allKeys || allKeys.length < 2) {
            return null;
        }
        let mainListingResource!: { resourceId: string; accessType: string };
        let listingCustomFieldsResource!: { resourceId: string; accessType: string };
        const secondaryListingPermission: { resourceId: string; accessType: string }[] = [];

        // Go over each listing / resourcePermission
        for (let i = 0; i < allKeys.length; i++) {
            const key = allKeys[i];
            const listing = control.value[key];
            // Take only the highest ranking permission into account
            const accessType = PermissionsControlComponent.getHighestRankingPermissionFromValue(listing);

            if (accessType) {
                const accessObject: any = {
                    resourceId: key,
                    accessType: accessType,
                };

                if (key === 'listings') {
                    mainListingResource = accessObject;
                } else if (key === 'customFields') {
                    listingCustomFieldsResource = accessObject;
                } else {
                    secondaryListingPermission.push(accessObject);
                }
            }
        }

        let valid = true;

        if (mainListingResource?.accessType) {
            valid = secondaryListingPermission?.some((x) => x?.accessType);
        } else if (secondaryListingPermission?.some((x) => x?.accessType)) {
            valid = !!mainListingResource?.accessType;
        }

        if (!valid) {
            return { listingsRequireListingsPermissionsError: true };
        }

        return null;
    }

    private static getHighestRankingPermissionFromValue(value: { [key: string]: boolean }): string {
        for (let j = PERMISSIONS_HIERARCHY.length - 1; j >= 0; j--) {
            const permissionKey = PERMISSIONS_HIERARCHY[j];

            if (value[permissionKey]) {
                return permissionKey.toUpperCase();
            }
        }

        return '';
    }

    /**
     * Whether the row belongs to a feature group the template renders. The global Listings row cascades only
     * to those, so a feature group that has a control but no row keeps the access type it was loaded with.
     */
    private isRenderedFeatureGroup(controlName: string): boolean {
        return Boolean(this.featureGroups?.some((featureGroup) => featureGroup.id === controlName));
    }

    onEditChange($event: Event) {
        const isChecked = ($event.target as HTMLInputElement).checked;
        const listingsGroup = this.formGroup.get('permissions.listings') as FormGroup;

        Object.keys(listingsGroup.controls).forEach((key) => {
            if (this.isRenderedFeatureGroup(key)) {
                const group = listingsGroup.get(key);
                const read = group?.get('read');
                const edit = group?.get('edit');
                if (isChecked) {
                    read?.enable({ onlySelf: false, emitEvent: false });
                    edit?.enable({ onlySelf: false, emitEvent: false });
                } else {
                    edit?.disable({ onlySelf: false, emitEvent: false });
                }

                if (read?.value === true) {
                    edit?.patchValue(isChecked, { emitEvent: true });
                }
            }
        });
    }

    onReadChange($event: Event) {
        const isChecked = ($event.target as HTMLInputElement).checked;
        const listingsGroup = this.formGroup.get('permissions.listings') as FormGroup;
        Object.keys(listingsGroup.controls).forEach((key) => {
            if (this.isRenderedFeatureGroup(key)) {
                const group = listingsGroup.get(key);
                const read = group?.get('read');
                const edit = group?.get('edit');

                const globalGroup = this.formGroup.get('permissions.listings.listings') as FormGroup | null;
                const globalRead = !!globalGroup?.get('read')?.value;
                const globalEdit = !!globalGroup?.get('edit')?.value;

                if (isChecked) {
                    if (globalRead) {
                        read?.enable({ onlySelf: false, emitEvent: false });
                    }
                    if (globalEdit) {
                        edit?.enable({ onlySelf: false, emitEvent: false });
                    }
                } else {
                    read?.disable({ onlySelf: false, emitEvent: false });
                    edit?.disable({ onlySelf: false, emitEvent: false });
                }
                read?.patchValue(isChecked, { emitEvent: true });
            }
        });
    }
}
